import {
  postHostedIdentifySessionChallenge,
  postHostedIdentifySessionChallengeVerify,
} from '@onefootprint/fetch-hosted';
import { getBiometricChallengeResponse } from '@/idv2/utils/get-biometric-challenge-response';
import { getLogger } from '@/idv2/utils/logger';

const { logWarn } = getLogger({ name: 'idv2', location: 'run-passkey-challenge' });

type StagedError = {
  stage: string;
  error: unknown;
};

export const isGetChallengeResponseError = (error: unknown): error is StagedError => {
  return (
    typeof error === 'object' &&
    error !== null &&
    'stage' in error &&
    (error as StagedError).stage === 'get-challenge-response'
  );
};

/**
 * Runs the passkey challenge flow for step-up authentication.
 * 1. Requests a biometric challenge from the server
 * 2. Prompts the user for biometric authentication via WebAuthn
 * 3. Verifies the challenge response with the server
 */
export const runPasskeyChallenge = async (identifyToken: string): Promise<void> => {
  const headers = { 'X-Fp-Authorization': identifyToken };

  const {
    data: { challengeData, error },
  } = await postHostedIdentifySessionChallenge({
    headers,
    body: { challengeKind: 'biometric' },
  });

  if (error) {
    throw error;
  }

  const { biometricChallengeJson, challengeKind } = challengeData;

  if (challengeKind !== 'biometric' || !biometricChallengeJson) {
    logWarn('Received incorrect challenge after requesting login biometric challenge');
    throw new Error('Received incorrect challenge type');
  }

  let challengeResponse: string;
  try {
    challengeResponse = await getBiometricChallengeResponse(biometricChallengeJson);
  } catch (error) {
    throw { stage: 'get-challenge-response', error };
  }

  await postHostedIdentifySessionChallengeVerify({
    headers,
    body: { challengeResponse },
  });
};
